import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import Modal from '@/components/common/Modal'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import Select from '@/components/common/Select'
import { toast } from 'sonner'
import { HOCKEY_AGE_GROUPS, getAvailableTiers } from '@/lib/hockeyData'
import { getAllowedAlertFrequencies, getSavedSearchLimit } from '@/lib/postingLimits'

/**
 * SaveSearchModal Component
 * 
 * Modal for creating or editing saved scrimmage searches
 */

export default function SaveSearchModal({ search, teamId, onSave, onCancel }) {
  const isEditing = !!search
  const [seasonTier, setSeasonTier] = useState('free')
  const [savedSearchCount, setSavedSearchCount] = useState(null)

  const [formData, setFormData] = useState({
    search_name: '',
    filter_level: '',
    filter_level_detail: '',
    filter_location_lat: null,
    filter_location_lng: null,
    filter_radius_miles: null,
    filter_date_start: '',
    filter_date_end: '',
    filter_posting_type: '',
    filter_referee_type: '',
    filter_home_and_home: false,
    conflict_mode: 'any',
    require_calendar_match: false,
    alert_enabled: false,
    alert_frequency: 'immediate'
  })

  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let mounted = true
    const loadTierAndCount = async () => {
      if (!teamId) return
      try {
        const [{ data: team }, { count }] = await Promise.all([
          supabase
            .from('teams')
            .select('season_tier')
            .eq('id', teamId)
            .single(),
          supabase
            .from('scrimmage_searches')
            .select('*', { count: 'exact', head: true })
            .eq('team_id', teamId)
        ])

        if (!mounted) return
        setSeasonTier(team?.season_tier || 'free')
        setSavedSearchCount(typeof count === 'number' ? count : null)
      } catch {
        if (!mounted) return
        setSeasonTier('free')
        setSavedSearchCount(null)
      }
    }

    loadTierAndCount()
    return () => {
      mounted = false
    }
  }, [teamId])

  useEffect(() => {
    if (search) {
      setFormData({
        search_name: search.search_name || '',
        filter_level: search.filter_level || '',
        filter_level_detail: search.filter_level_detail || '',
        filter_location_lat: search.filter_location_lat,
        filter_location_lng: search.filter_location_lng,
        filter_radius_miles: search.filter_radius_miles,
        filter_date_start: search.filter_date_start || '',
        filter_date_end: search.filter_date_end || '',
        filter_posting_type: search.filter_posting_type || '',
        filter_referee_type: search.filter_referee_type || '',
        filter_home_and_home: search.filter_home_and_home || false,
        conflict_mode: search.conflict_mode || 'any',
        require_calendar_match: search.require_calendar_match || false,
        alert_enabled: search.alert_enabled || false,
        alert_frequency: search.alert_frequency || 'immediate'
      })
    }
  }, [search])

  // Coerce alert frequency to what's allowed for the current tier
  useEffect(() => {
    const allowed = getAllowedAlertFrequencies(seasonTier)
    if (!allowed.includes(formData.alert_frequency)) {
      setFormData(prev => ({
        ...prev,
        alert_frequency: allowed[0] || 'daily'
      }))
    }
  }, [seasonTier, formData.alert_frequency])

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.search_name.trim()) {
      toast.error('Please enter a search name')
      return
    }

    setLoading(true)

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        toast.error('Please sign in to save searches')
        return
      }

      // Enforce saved search limit on Free tier (new searches only)
      if (!isEditing) {
        const limit = getSavedSearchLimit(seasonTier)
        if (limit !== Infinity) {
          const { count } = await supabase
            .from('scrimmage_searches')
            .select('*', { count: 'exact', head: true })
            .eq('team_id', teamId)

          if (typeof count === 'number' && count >= limit) {
            toast.error(`Free tier limit reached (${limit} saved searches). Upgrade to Pro for unlimited.`)
            return
          }
        }
      }

      const allowedFrequencies = getAllowedAlertFrequencies(seasonTier)
      const safeFrequency = allowedFrequencies.includes(formData.alert_frequency)
        ? formData.alert_frequency
        : (allowedFrequencies[0] || 'daily')

      const payload = {
        team_id: teamId,
        user_id: session.user.id,
        search_name: formData.search_name.trim(),
        filter_level: formData.filter_level || null,
        filter_level_detail: formData.filter_level_detail || null,
        filter_location_lat: formData.filter_location_lat,
        filter_location_lng: formData.filter_location_lng,
        filter_radius_miles: formData.filter_radius_miles,
        filter_date_start: formData.filter_date_start || null,
        filter_date_end: formData.filter_date_end || null,
        filter_posting_type: formData.filter_posting_type || null,
        filter_referee_type: formData.filter_referee_type || null,
        filter_home_and_home: formData.filter_home_and_home || null,
        conflict_mode: formData.conflict_mode || null,
        require_calendar_match: formData.require_calendar_match || false,
        alert_enabled: formData.alert_enabled || false,
        alert_frequency: safeFrequency
      }

      if (isEditing) {
        const { error } = await supabase
          .from('scrimmage_searches')
          .update(payload)
          .eq('id', search.id)

        if (error) throw error
        toast.success('Search updated')
      } else {
        const { error } = await supabase
          .from('scrimmage_searches')
          .insert([payload])

        if (error) throw error
        toast.success('Search saved')
      }

      onSave()
    } catch (error) {
      console.error('[SaveSearchModal] Error saving search:', error)
      toast.error('Failed to save search')
    } finally {
      setLoading(false)
    }
  }

  const availableTiers = formData.filter_level 
    ? [{ value: '', label: 'All tiers' }, ...getAvailableTiers(formData.filter_level)]
    : [{ value: '', label: 'All tiers' }]

  const allowedAlertFrequencies = getAllowedAlertFrequencies(seasonTier)
  const alertFrequencyOptions = [
    { value: 'immediate', label: 'Immediate (within 15 min)' },
    { value: 'daily', label: 'Daily Digest' },
    { value: 'weekly', label: 'Weekly Digest' }
  ].filter(opt => allowedAlertFrequencies.includes(opt.value))

  const limit = getSavedSearchLimit(seasonTier)
  const atSavedSearchLimit = !isEditing && limit !== Infinity && typeof savedSearchCount === 'number' && savedSearchCount >= limit

  return (
    <Modal
      isOpen={true}
      onClose={onCancel}
      title={isEditing ? 'Edit Saved Search' : 'Save New Search'}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Search Name */}
        <Input
          label="Search Name"
          value={formData.search_name}
          onChange={(e) => handleChange('search_name', e.target.value)}
          placeholder="e.g., Weekend Squirt B1 Games"
          required
        />

        {/* Age Group */}
        <Select
          label="Age Group"
          value={formData.filter_level}
          onChange={(e) => {
            handleChange('filter_level', e.target.value)
            handleChange('filter_level_detail', '') // Reset tier
          }}
          options={[
            { value: '', label: 'All age groups' },
            ...HOCKEY_AGE_GROUPS
          ]}
        />

        {/* Team Tier */}
        <Select
          label="Team Tier"
          value={formData.filter_level_detail}
          onChange={(e) => handleChange('filter_level_detail', e.target.value)}
          options={availableTiers}
          disabled={!formData.filter_level}
        />

        {/* Date Range */}
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="From Date"
            type="date"
            value={formData.filter_date_start}
            onChange={(e) => handleChange('filter_date_start', e.target.value)}
          />
          <Input
            label="To Date"
            type="date"
            value={formData.filter_date_end}
            onChange={(e) => handleChange('filter_date_end', e.target.value)}
          />
        </div>

        {/* Posting Type */}
        <Select
          label="Type"
          value={formData.filter_posting_type}
          onChange={(e) => handleChange('filter_posting_type', e.target.value)}
          options={[
            { value: '', label: 'All Types' },
            { value: 'available', label: 'Home Ice Available' },
            { value: 'wanted', label: 'Seeking Away Game' }
          ]}
        />

        {/* Calendar Matching */}
        <div className="border-t border-gray-200 pt-4">
          <h4 className="font-semibold text-brand-charcoal mb-3">Calendar Matching</h4>
          
          <label className="flex items-center mb-3">
            <input
              type="checkbox"
              checked={formData.require_calendar_match}
              onChange={(e) => handleChange('require_calendar_match', e.target.checked)}
              className="mr-2 h-4 w-4 text-brand-green border-gray-300 rounded focus:ring-brand-green"
            />
            <span className="text-sm text-brand-charcoal">Match with my calendar</span>
          </label>

          {formData.require_calendar_match && (
            <Select
              label="Conflict Mode"
              value={formData.conflict_mode}
              onChange={(e) => handleChange('conflict_mode', e.target.value)}
              options={[
                { value: 'strict', label: '🟢 Only free days' },
                { value: 'flexible', label: '🟡 Non-overlapping times' },
                { value: 'any', label: '🔴 Show all (with warnings)' }
              ]}
            />
          )}
        </div>

        {/* Email Alerts */}
        <div className="border-t border-gray-200 pt-4">
          <h4 className="font-semibold text-brand-charcoal mb-3">Email Alerts</h4>
          
          <label className="flex items-center mb-3">
            <input
              type="checkbox"
              checked={formData.alert_enabled}
              onChange={(e) => handleChange('alert_enabled', e.target.checked)}
              className="mr-2 h-4 w-4 text-brand-green border-gray-300 rounded focus:ring-brand-green"
            />
            <span className="text-sm text-brand-charcoal">Send me email alerts for new matches</span>
          </label>

          {formData.alert_enabled && (
            <Select
              label="Alert Frequency"
              value={formData.alert_frequency}
              onChange={(e) => handleChange('alert_frequency', e.target.value)}
              options={alertFrequencyOptions}
            />
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <Button
            type="submit"
            variant="primary"
            disabled={loading || atSavedSearchLimit}
            loading={loading}
            className="flex-1"
          >
            {isEditing ? 'Update Search' : 'Save Search'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
        </div>

        {atSavedSearchLimit && (
          <p className="text-xs text-brand-slate">
            Free tier limit reached ({limit} saved searches).{' '}
            <a href="/pricing" className="text-brand-green hover:underline">Upgrade to Pro</a> for unlimited.
          </p>
        )}
      </form>
    </Modal>
  )
}

